import { useCallback } from 'react';
import { di } from 'react-magnetic-di';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useService } from '@atlassian/help-center-common-util/use-service';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';

import { usePortalId, useRequestTypeId } from '../../controllers/request-create-context-provider';
import { proformaGasAnalyticsFetch } from '../../common/services/proforma-gas-analytics-fetch';
import { JSM_CUSTOMER_PORTAL_URL_PREFIX } from '../constants';

export interface JiraLabelsResponse {
	suggestions: JiraLabel[];
}

interface JiraLabel {
	label: string;
	html: string;
}

/**
 * Label search service for ProForma label fields.
 *
 * Uses portalId because this calls a JSM customer portal REST API
 * (/rest/servicedesk/1/customer/portal/{portalId}/...), NOT a ProForma Stargate endpoint.
 *
 * See docs/PORTAL_ID_AND_SERVICE_DESK_ID.md for the distinction.
 */
export const useLabelsService = () => {
	di(usePortalId, useRequestTypeId);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const errorUtils = usePfErrorUtils();
	const portalId = usePortalId();
	const requestTypeId = useRequestTypeId();

	const request = useCallback(
		({ customFieldId, query }: { customFieldId?: string; query: string }) =>
			proformaGasAnalyticsFetch<JiraLabelsResponse>(
				`${JSM_CUSTOMER_PORTAL_URL_PREFIX}/${portalId}/requestType/${requestTypeId}/labels/suggest?${
					customFieldId === undefined ? '' : `customFieldId=${customFieldId}&`
				}query=${encodeURIComponent(query)}`,
				{ method: 'GET' },
				createAnalyticsEvent,
				errorUtils,
			),
		[portalId, requestTypeId, createAnalyticsEvent, errorUtils],
	);

	return useService(request);
};
